import React from 'react';
import { createRoot } from 'react-dom/client';
import AppContainer from 'components/AppContainer';
import { ModalProvider } from 'components/core/modal/Context';

import detectEvents from './detect';

// Assume className is simple and can be found at top-level (window).
// Fallback to eval to handle cases like 'My.React.ComponentName'.
// Also, try to gracefully import Babel 6 style default exports
const topLevel = typeof window === 'undefined' ? this : window;

function constructorFromGlobal(className) {
  let constructor;

  if ('ReactComponents' in topLevel) {
    constructor = topLevel.ReactComponents[className];
  } else {
    constructor = topLevel[className];
  }
  // Try to access the class globally first

  // If that didn't work, try eval
  if (!constructor) {
    // eslint-disable-next-line no-new-func
    constructor = Function(`'use strict'; return (${className})`)();
  }

  // Lastly, if there is a default attribute try that
  if (constructor && constructor['default']) {
    constructor = constructor['default'];
  }

  return constructor;
}

const ReactUJS = {
  // This attribute holds the name of component which should be mounted
  // example: `data-react-class="MyApp.Items.EditForm"`
  CLASS_NAME_ATTR: 'data-react-class',

  // This attribute holds JSON stringified props for initializing the component
  // example: `data-react-props="{\"item\": { \"id\": 1, \"name\": \"My Item\"} }"`
  PROPS_ATTR: 'data-react-props',

  // A unique identifier to identify a node
  CACHE_ID_ATTR: 'data-react-cache-id',

  TURBO_PERMANENT_ATTR: 'data-turbo-permanent',

  components: {},
  rootComponents: [],

  // helper method for the mount and unmount methods to find the
  // `data-react-class` DOM elements
  findDOMNodes(searchSelector) {
    const classNameAttr = ReactUJS.CLASS_NAME_ATTR;
    // we will use fully qualified paths as we do not bind the callbacks
    let selector;
    let parent;

    switch (typeof searchSelector) {
      case 'undefined':
        selector = `[${classNameAttr}]`;
        parent = document;
        break;
      case 'object':
        selector = `[${classNameAttr}]`;
        parent = searchSelector;
        break;
      case 'string':
        selector = `${searchSelector}[${classNameAttr}], ${searchSelector} [${classNameAttr}]`;
        parent = document;
        break;
      default:
        break;
    }

    return parent.querySelectorAll(selector);
  },

  getConstructor: constructorFromGlobal,

  // Within `searchSelector`, find nodes which should have React components
  // inside them, and mount them with their props.
  mountComponents(searchSelector) {
    const ujs = ReactUJS;
    const nodes = ujs.findDOMNodes(searchSelector);

    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const className = node.getAttribute(ujs.CLASS_NAME_ATTR);
      const constructor = ujs.getConstructor(className);
      const propsJson = node.getAttribute(ujs.PROPS_ATTR);
      const props = propsJson && JSON.parse(propsJson);
      const cacheId = node.getAttribute(ujs.CACHE_ID_ATTR);
      const turboPermanent = node.hasAttribute(ujs.TURBO_PERMANENT_ATTR);

      if (!constructor) {
        const message = `Cannot find component: '${className}'`;
        throw new Error(
          `${message}. Make sure your component is available to render.`,
        );
      } else {
        let component = this.components[cacheId];
        if (component === undefined) {
          component = React.createElement(constructor, props);
          if (turboPermanent) {
            this.components[cacheId] = component;
          }
        }
        const root = createRoot(node);
        this.rootComponents.push(root);
        component = root.render(
          <AppContainer
            reactClassForV3ApiClient={node.getAttribute('data-react-class')}
            modalMountId="main"
          >
            <ModalProvider>{component}</ModalProvider>
          </AppContainer>,
        );
      }
    }
  },

  unmountComponents() {
    for (let i = 0; i < this.rootComponents.length; i += 1) {
      this.rootComponents[i].unmount();
    }
  },

  // Check the global context for installed libraries
  // and figure out which library to hook up to (pjax, Turbo, jQuery)
  // This is called on load, but you can call it again if needed
  // (It will unmount itself)
  detectEvents() {
    detectEvents(this);
  },
};

// These stable references are so that handlers can be added and removed:
ReactUJS.handleMount = function (e) {
  let target;
  if (e && e.target) {
    target = e.target;
  }
  ReactUJS.mountComponents(target);
};
ReactUJS.handleUnmount = function (e) {
  let target;
  if (e && e.target) {
    target = e.target;
  }
  ReactUJS.unmountComponents(target);
};

if (typeof window !== 'undefined') {
  // Only setup events for browser (not server-rendering)
  ReactUJS.detectEvents();
}

// It's a bit of a no-no to populate the global namespace,
// but we really need it!
// We need access to this object for server rendering, and
// we can't do a dynamic `require`, so we'll grab it from here:

export default ReactUJS;
